import React from 'react';

// --- Sub-component: Container ---
interface CardContainerProps {
  children: React.ReactNode;
  className?: string;
  'aria-label'?: string;
}
const CardContainer: React.FC<CardContainerProps> = ({ children, className, 'aria-label': ariaLabel }) => {
  return (
    <div
      role="listitem"
      aria-label={ariaLabel}
      className={`flex h-12 py-2 px-3 items-center gap-2 flex-1 rounded-[16px] border border-[#E5E8EB] bg-[#FFF] ${className || ''}`}
    >
      {children}
    </div>
  );
};
CardContainer.displayName = 'DataRankCard.Container';

// --- Sub-component: Rank ---
interface RankDisplayProps {
  rank: number | string;
  className?: string;
}
const RankDisplay: React.FC<RankDisplayProps> = ({ rank, className }) => {
  return (
    <div
      role="presentation"
      aria-hidden="true"
      className={`w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-full bg-[#5DB8B3] ${className || ''}`}
    >
      <span
        className="text-white text-center text-xs font-bold leading-none"
      >
        {rank}
      </span>
    </div>
  );
};
RankDisplay.displayName = 'DataRankCard.Rank';

// --- Sub-component: Text ---
interface TextDisplayProps {
  text: string;
  className?: string;
}
const TextDisplay: React.FC<TextDisplayProps> = ({ text, className }) => {
  return (
    <span
      className={`text-[#16282D] text-sm font-medium leading-6 ${className || ''}`}
    >
      {text}
    </span>
  );
};
TextDisplay.displayName = 'DataRankCard.Text';

// --- Main Composed Component Props ---
interface DataRankCardProps {
  rank: number | string;
  text: string;
  className?: string;
  'aria-label'?: string;
}

// Base component for default composition
const BaseDataRankCard: React.FC<DataRankCardProps> = ({ rank, text, className, 'aria-label': ariaLabel }) => {
  const defaultAriaLabel = `Rank ${rank}: ${text}`;

  return (
    <CardContainer
      className={className}
      aria-label={ariaLabel || defaultAriaLabel}
    >
      <RankDisplay rank={rank} />
      <TextDisplay text={text} />
    </CardContainer>
  );
};

// --- Compound Component Type and Export ---
type DataRankCardType = React.FC<DataRankCardProps> & {
  Container: React.FC<CardContainerProps>;
  Rank: React.FC<RankDisplayProps>;
  Text: React.FC<TextDisplayProps>;
};

export const DataRankCard = BaseDataRankCard as DataRankCardType;
DataRankCard.Container = CardContainer;
DataRankCard.Rank = RankDisplay;
DataRankCard.Text = TextDisplay;
DataRankCard.displayName = 'DataRankCard';
